// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {getProjectId} from 'common/report/util';
import {PositionParent} from 'common/components/drop-menu';
import {SelectedPropertyItem} from 'common/query-builder/query-block/properties-menu/types';

import css from './index.inline.styl';
import {template} from './template';
import {ActionsPopoverAttrs, ActionsPopoverProps, ActionsPopoverState, VIEWS} from './types';

const STATE_RESET = Object.freeze({
  dropMenuOpen: false,
  view: null,
  positionParent: null,
} as ActionsPopoverState);

/**
 * ActionsPopover is responsible for rendering an mp-popover-card component
 *
 * Depending on which item you click, ActionsPopover will either emit an 'action' event and
 * close the component or display another view.
 */
export class ActionsPopover extends Component<ActionsPopoverState, ActionsPopoverAttrs> {
  static get attrsSchema(): AttrsSchema<ActionsPopoverAttrs> {
    return {
      ...super.attrsSchema,
      open: {type: `boolean`},

      // attributes for VIEWS.POPOVER
      'is-segmented': {type: `boolean`},
      title: {type: `string`},
      subtitle: {type: `string`},
      /**
       * button-options are passed directly to mp-popover-card
       * the next view after selecting a button option in the popover card is dictated
       * by the `value` of the button option
       * 1. value === VIEWS.BREAKDOWN => the next view will be VIEWS.BREAKDOWN
       * 2. value === the value of `type` in one of cohort-details => the next view will be VIEWS.CREATE_COHORT
       * 3. if value doesn't match any of the above => the popover will close
       */
      'button-options': {type: `json`, description: `interface: ButtonOption[]`, default: []},

      // attributes for VIEWS.BREAKDOWN
      'breakdown-options': {type: `json`, default: {}},
      event: {type: `string`},
      'project-id': {type: `number`},
      'workspace-id': {type: `number`},

      'prevent-close-selectors': {
        type: `json`,
        description: `interface: string[] - list of selectors that shouldn't trigger drop menu to close`,
        default: [],
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {...STATE_RESET},
      helpers: {
        handlePopoverAction: (ev) => {
          const {value} = ev.detail.item;
          if (value === VIEWS.BREAKDOWN) {
            this.update({view: VIEWS.BREAKDOWN});
          } else {
            this.dispatchEvent(new CustomEvent(`action`, {detail: {action: value}}));
            this.update({dropMenuOpen: false});
          }
        },
        handlePopoverClose: () => {
          this.dispatchEvent(new CustomEvent(`close`));
        },
        handleBreakdownMenuSelect: (item: SelectedPropertyItem) => {
          this.dispatchEvent(
            new CustomEvent(`action`, {
              detail: {
                action: `breakdown`,
                value: item.item,
              },
            }),
          );
        },
        breakdownDisabledItemsMap: () =>
          (this.attr(`breakdown-options`).disabledItems || []).reduce((map, item) => ({...map, [item]: true}), {}),
        handleDropMenuChange: (ev) => {
          if (ev.detail.state === `closed`) {
            this.helpers.handlePopoverClose();
          }
        },

        getProjectId: () => this.attr(`project-id`) || parseInt(getProjectId(), 10),
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
  }

  set positionParent(positionParent: PositionParent) {
    this.update({positionParent});
  }

  attributeChangedCallback(attr: string, oldValue: string, newValue: string) {
    super.attributeChangedCallback(attr, oldValue, newValue);
    if (attr === `open`) {
      if (this.attr(`open`)) {
        this.update({dropMenuOpen: true});
      } else if (this.initialized) {
        this.reset();
      }
    }
  }

  reset() {
    this.update(STATE_RESET);
  }
}

export const ActionsPopoverEl = defineAndMakeJsxFactory<ActionsPopoverProps>(`mp-actions-popover`, ActionsPopover);
export const ActionsPopoverReact = panel2react<ActionsPopoverProps>(`mp-actions-popover`, ActionsPopover);
